import { useRequestStatus } from 'RequestStatusModule/hooks/useRequestStatus';
import { useArrayTagsInput } from 'hooks/useArrayItems/useArrayTagsInput';
import { useEffect, useRef, useState, ComponentProps, FormEvent } from 'react';
import { TenantTypes } from 'TenantModule/constants';
import { CreateTenantPayload } from 'TenantModule/types';
import { useQueryClient } from '@tanstack/react-query';
import { cohortHasRoom } from 'TenantModule/api/getCohortsApi';
import { useCohortsQuery } from 'TenantModule/api/queries/useCohortsQuery';
import { cohortQueryKeys } from 'TenantModule/api/queries/cohortQueryKeys';
import { useInfraRequestServicesQuery } from 'TenantModule/api/queries/useInfraRequestServicesQuery';
import { useDispatch } from 'react-redux';
import { createTenant } from 'thunk';
import { csmHashById } from 'CsmModule/constants';
import { useCsmSelector } from 'CsmModule/hooks/useCsmSelector';
import { CsmSelector } from 'CsmModule/components/CsmSelector';
import { preventFormSubmissionOnPressEnter } from 'utils/form';
import { useNotification } from 'hooks/useNotification';

// tags live in useArrayTagsInput and the CSM in useCsmSelector — only the two
// text fields are form state here.
type NewTenantInitialValues = {
  tenantName: string;
  secretName: string;
};

const newTenantInitialValues = {
  tenantName: '',
  secretName: '',
};

const requestStatusId = 'create-tenant-card';

// RDM-2277: when tenant stack PRs are armed, the name becomes the k8s namespace, the
// DNS zone and the IAM role prefix — the server enforces this rule.
export const INFRA_TENANT_NAME_PATTERN = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

/** `infra` for the create payload — undefined unless at least one option is set. */
export const buildCreateTenantInfra = ({
  services,
  vanityDomain,
}: {
  services: string[];
  vanityDomain: string;
}): CreateTenantPayload['infra'] => {
  const trimmedDomain = vanityDomain.trim();
  if (!services.length && !trimmedDomain) return undefined;
  return {
    ...(services.length ? { services } : {}),
    ...(trimmedDomain ? { vanityDomain: trimmedDomain } : {}),
  };
};

// Default Shared: a form that omits tenantType created a Shared tenant before
// RDM-2031, and the regression-free acceptance criterion pins that behavior.
const defaultTenantType = TenantTypes.Shared;

export const useCreateTenantFormFields = () => {
  const teamNameRef = useRef(document.createElement('input'));

  const onCreateTenantFormKeyDown = preventFormSubmissionOnPressEnter;

  const {
    addTag,
    removeTag,
    inputRef,
    tagsList,
    tagError,
    setTagsList,
    clearErrorAndTags,
    handleCheckboxChange,
    standardTags,
    clearStandardTags,
    setStandardTags,
  } = useArrayTagsInput();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { requestStatus } = useRequestStatus({
    requestStatusId,
  });
  const isRequesting = Boolean(requestStatus?.isRequesting);
  const { onCsmSelectorClick, selectedCsm } = useCsmSelector({
    accountId: null,
  });
  const [createTenantParams, setcreateTenantParams] =
    useState<NewTenantInitialValues>(newTenantInitialValues);
  const [tenantType, setTenantType] = useState<TenantTypes>(defaultTenantType);
  const [cohortId, setCohortId] = useState<number | ''>('');
  // Tenant stack PR options (RDM-2277) — only surfaced when the backend says armed.
  const { enabled: isInfraRequestEnabled, services: infraServiceOptions } =
    useInfraRequestServicesQuery();
  const [infraServices, setInfraServices] = useState<string[]>([]);
  const [vanityDomain, setVanityDomain] = useState('');
  const isTenantNameInvalidForInfra =
    isInfraRequestEnabled &&
    createTenantParams.tenantName !== '' &&
    !INFRA_TENANT_NAME_PATTERN.test(createTenantParams.tenantName);
  const isEnterprise = tenantType === TenantTypes.Enterprise;
  // Cohorts are only fetched once the operator flips to Enterprise.
  const {
    cohorts,
    isLoading: isLoadingCohorts,
    isFetching: isFetchingCohorts,
    isError: isCohortsError,
  } = useCohortsQuery({
    enabled: isEnterprise,
  });

  // Default the picker to the least-filled cohort with room (the server orders the
  // list that way); flipping back to a dedicated type clears the pick so a stale
  // cohortId can never ride along in a Shared/Single payload.
  //
  // RECONCILES on every load, not just when the slot is empty: capacity moves while
  // the form is open (another operator seats a tenant), so a cohort that was pickable
  // a minute ago can be full or archived by submit time. Leaving it selected would
  // send a request the server 409s.
  useEffect(() => {
    if (!isEnterprise) {
      setCohortId('');
      return;
    }
    if (cohorts.length === 0) {
      // Do NOT clear the pick. An empty list is frequently TRANSIENT (a refetch blip),
      // and clearing here silently swapped the operator's choice for the least-filled
      // default as soon as the list came back — a wrong-cohort risk with no visible
      // cue. The reason clearing existed was to stop a stale cohortId shipping after
      // the list went away; submit is blocked while the list is empty instead, which
      // holds that invariant without discarding the operator's intent.
      return;
    }
    const picked = cohortId === '' ? undefined : cohorts.find((c) => c.id === cohortId);
    if (!picked || !cohortHasRoom(picked)) {
      const firstWithRoom = cohorts.find(cohortHasRoom);
      setCohortId(firstWithRoom ? firstWithRoom.id : '');
    }
  }, [isEnterprise, cohorts, cohortId]);

  // Deliberately NOT blocked on isFetching. That was the round-3 answer to "a cached
  // pick can be stale during revalidation", but the gate below now validates the pick
  // against the current list directly, which is the targeted version of the same
  // protection. Keeping both meant Submit went dead on every background refetch — and
  // with a 30s staleTime plus refetch-on-focus, that is most of the time an operator
  // is actually looking at the form. The residual race (the in-flight response would
  // have shown the pick full) is unavoidable, cheap, and already handled: the server
  // 409s, showError surfaces it, and onFailure invalidates so the reconcile effect
  // re-picks.
  // Validates the pick against the CURRENT list rather than trusting the reconcile
  // effect: when a refetch settles, isFetching drops to false in the same render that
  // still holds the old cohortId, and the effect only corrects it on the NEXT one. In
  // that window Submit was live with a pick the refresh had just invalidated — the
  // avoidable rejection the reconciliation exists to prevent.
  const pickedCohort = cohortId === '' ? undefined : cohorts.find((c) => c.id === cohortId);
  const isSubmitBlocked =
    isEnterprise &&
    (cohortId === '' ||
      cohorts.length === 0 ||
      !pickedCohort ||
      !cohortHasRoom(pickedCohort) ||
      isLoadingCohorts ||
      isCohortsError);

  const onSubmitCreateTenantForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Enforced here, not only on the button: Enter on another field,
    // form.requestSubmit(), or any programmatic submit bypasses a disabled button
    // and would dispatch an Enterprise create with no resolvable cohort.
    if (isSubmitBlocked) {
      return;
    }
    const csmAssigned = selectedCsm ? csmHashById[selectedCsm.accountId] : undefined;
    // Hidden fields never ride along: with the feature unarmed nothing is sent.
    const infra = isInfraRequestEnabled
      ? buildCreateTenantInfra({ services: infraServices, vanityDomain })
      : undefined;

    // The JWT secret is server-generated since GLX-1813 (the old top-level jwtToken
    // field never reached the server's tenant.jwtToken anyway). creator is derived
    // from the auth token server-side.
    const params: CreateTenantPayload & { meta: object } = {
      tenant: {
        tenantName: createTenantParams.tenantName,
        // Omitted when unassigned — the API treats csm as optional, and an empty
        // string would be stored verbatim.
        ...(csmAssigned?.name ? { csm: csmAssigned.name } : {}),
        tags: tagsList,
        tenantType,
        // Enterprise group filling (RDM-2031): the picked cohort decides the
        // cluster; dedicated types must never send one (the server 400s it).
        ...(isEnterprise && cohortId !== '' ? { cohortId } : {}),
      },
      // Enterprise derives the cluster secret FROM the cohort — the server rejects
      // an Enterprise body that supplies one.
      ...(isEnterprise ? {} : { secret: { secretName: createTenantParams.secretName } }),
      ...(infra ? { infra } : {}),
      meta: {
        requestStatusId,
        onSuccess: () => {
          showSuccess('Tenant is created. Please check Tenant page.');
          setcreateTenantParams(newTenantInitialValues);
          setTagsList([]);
          const consumedSeat = isEnterprise;
          setTenantType(defaultTenantType);
          setCohortId('');
          setInfraServices([]);
          setVanityDomain('');
          // Only an Enterprise create consumed a seat; refreshing after a
          // Shared/Single create is a pointless round trip. lists()
          // partial-matches both the picker and the admin query.
          if (consumedSeat) {
            queryClient.invalidateQueries({ queryKey: cohortQueryKeys.lists() });
          }
        },
        onFailure: ({ error }: { error?: any } = {}) => {
          // Surface the server's reason (409 duplicate, 400 validation, ...) instead
          // of a generic retry message.
          const apiMessage =
            error?.data?.message || error?.response?.data?.message || error?.message;
          showError(
            apiMessage
              ? `Tenant cannot be created: ${apiMessage}`
              : 'Tenant cannot be created, please try again',
          );
          // An Enterprise create can lose the (unavoidable) capacity race: the cohort
          // filled between the picker's render and the claim. Without a refresh the
          // list stays fresh for its staleTime with the now-full cohort still
          // selected, so every retry re-submits the same rejected cohortId. Refetching
          // lets the reconcile effect move the selection to a cohort with room. Fired
          // for any Enterprise failure, not just 409s — the seat state is worth one
          // GET regardless, and the error shape is not a reliable status
          // discriminator across the saga's failure paths.
          if (isEnterprise) {
            queryClient.invalidateQueries({ queryKey: cohortQueryKeys.lists() });
          }
        },
      },
    };
    // TODO: Fix with Redux Toolkit configureStore migration
    dispatch(createTenant(params) as any);
  };

  const onCreateTenantFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setcreateTenantParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const onDeleteTag = (tag: string) => {
    if (isRequesting) return;
    removeTag(tag);
  };

  const handleCsmSelectorClick: ComponentProps<typeof CsmSelector>['onClick'] = (e) => {
    onCsmSelectorClick({ target: e.currentTarget });
  };

  return {
    addTag,
    inputRef,
    tagsList,
    tagError,
    clearErrorAndTags,
    handleCheckboxChange,
    standardTags,
    clearStandardTags,
    teamNameRef,
    setStandardTags,
    onCreateTenantFormKeyDown,
    onSubmitCreateTenantForm,
    onCreateTenantFormChange,
    createTenantParams,
    isRequesting,
    selectedCsm,
    handleCsmSelectorClick,
    onDeleteTag,
    tenantType,
    setTenantType,
    cohortId,
    setCohortId,
    isEnterprise,
    cohorts,
    isLoadingCohorts,
    isCohortsError,
    // Enterprise needs a resolvable cohort before the form may submit — including
    // while the list is still loading or failed to load, when any previously
    // defaulted pick cannot be trusted.
    isSubmitBlocked,
    isInfraRequestEnabled,
    infraServiceOptions,
    infraServices,
    setInfraServices,
    vanityDomain,
    setVanityDomain,
    isTenantNameInvalidForInfra,
  };
};
